export type OficinaAvaliacaoResumo = {
  id: string
  status: string
  share_token?: string | null
  cliente_nome?: string | null
  cliente_telefone?: string | null
  cliente_assinado_em?: string | null
  atendimento_finalizado_em?: string | null
}

export type OficinaDiagnosticoResumo = {
  id: string
  avaliacao_id?: string | null
  status: string
  share_token?: string | null
  cliente_ciente_em?: string | null
  cliente_autorizou_em?: string | null
}

export type OficinaAtendimentoStatus =
  | "sem_entrada"
  | "entrada_criada"
  | "entrada_finalizada"
  | "termo_emitido"
  | "cliente_ciente_termo"
  | "diagnostico_criado"
  | "diagnostico_enviado"
  | "cliente_ciente_diagnostico"
  | "cliente_autorizou"
  | "finalizado"

export type OficinaAtendimento = {
  status: OficinaAtendimentoStatus
  label: string
  proximaAcao: string
  helper: string
  prioridade: number
}

export type OficinaActionKind =
  | "start_entry"
  | "continue_entry"
  | "view_entry"
  | "emit_term"
  | "send_term"
  | "finalize"
  | "view_service"
  | "create_diagnostic"
  | "send_diagnostic"
  | "view_diagnostic"
  | "wait_customer"
  | "view_vehicle"

export type OficinaVehicleAction = {
  kind: OficinaActionKind
  label: string
  href?: string
  variant?: "default" | "outline" | "ghost"
  disabled?: boolean
}

const STATUS_META: Record<OficinaAtendimentoStatus, OficinaAtendimento> = {
  sem_entrada: {
    status: "sem_entrada",
    label: "Veículo cadastrado",
    proximaAcao: "Iniciar entrada",
    helper: "Crie a entrada para registrar o estado do veículo.",
    prioridade: 0,
  },
  entrada_criada: {
    status: "entrada_criada",
    label: "Entrada criada",
    proximaAcao: "Continuar entrada",
    helper: "Complete os dados e evidências principais da entrada.",
    prioridade: 1,
  },
  entrada_finalizada: {
    status: "entrada_finalizada",
    label: "Entrada finalizada",
    proximaAcao: "Emitir termo",
    helper: "A entrada já pode gerar o termo de ciência.",
    prioridade: 2,
  },
  termo_emitido: {
    status: "termo_emitido",
    label: "Termo enviado",
    proximaAcao: "Criar diagnóstico",
    helper: "O termo está pronto para conferência do cliente.",
    prioridade: 3,
  },
  cliente_ciente_termo: {
    status: "cliente_ciente_termo",
    label: "Cliente ciente do termo",
    proximaAcao: "Finalizar atendimento",
    helper: "A ciência do termo foi registrada. O diagnóstico é opcional.",
    prioridade: 4,
  },
  diagnostico_criado: {
    status: "diagnostico_criado",
    label: "Diagnóstico criado",
    proximaAcao: "Enviar diagnóstico",
    helper: "Revise os itens e envie o diagnóstico ao cliente.",
    prioridade: 5,
  },
  diagnostico_enviado: {
    status: "diagnostico_enviado",
    label: "Diagnóstico enviado",
    proximaAcao: "Aguardando cliente",
    helper: "O link do diagnóstico já foi compartilhado.",
    prioridade: 6,
  },
  cliente_ciente_diagnostico: {
    status: "cliente_ciente_diagnostico",
    label: "Cliente ciente do diagnóstico",
    proximaAcao: "Finalizar atendimento",
    helper: "O cliente registrou ciência do diagnóstico.",
    prioridade: 7,
  },
  cliente_autorizou: {
    status: "cliente_autorizou",
    label: "Serviço autorizado",
    proximaAcao: "Finalizar atendimento",
    helper: "O cliente autorizou o serviço pelo link público.",
    prioridade: 8,
  },
  finalizado: {
    status: "finalizado",
    label: "Atendimento finalizado",
    proximaAcao: "Ver histórico",
    helper: "Entrada, termo e diagnóstico já têm registro suficiente.",
    prioridade: 9,
  },
}

export const getOficinaAtendimentoStatus = (
  avaliacao?: OficinaAvaliacaoResumo | null,
  diagnosticos: OficinaDiagnosticoResumo[] = []
): OficinaAtendimento => {
  if (!avaliacao) return STATUS_META.sem_entrada
  if (avaliacao.atendimento_finalizado_em) return STATUS_META.finalizado

  const diagnosticosDaEntrada = diagnosticos.filter((diagnostico) =>
    diagnostico.avaliacao_id ? diagnostico.avaliacao_id === avaliacao.id : true
  )
  const diagnosticoMaisAvancado = diagnosticosDaEntrada.reduce<OficinaDiagnosticoResumo | null>((atual, diagnostico) => {
    if (!atual) return diagnostico
    const prioridadeAtual = getDiagnosticoPrioridade(atual)
    const prioridadeNovo = getDiagnosticoPrioridade(diagnostico)
    return prioridadeNovo > prioridadeAtual ? diagnostico : atual
  }, null)

  if (diagnosticoMaisAvancado?.cliente_autorizou_em || diagnosticoMaisAvancado?.status === "autorizado") {
    return STATUS_META.cliente_autorizou
  }

  if (diagnosticoMaisAvancado?.cliente_ciente_em || diagnosticoMaisAvancado?.status === "ciente") {
    return STATUS_META.cliente_ciente_diagnostico
  }

  if (diagnosticoMaisAvancado?.share_token || diagnosticoMaisAvancado?.status === "compartilhado") {
    return STATUS_META.diagnostico_enviado
  }

  if (diagnosticoMaisAvancado) return STATUS_META.diagnostico_criado

  if (avaliacao.cliente_assinado_em) return STATUS_META.cliente_ciente_termo
  if (avaliacao.share_token) return STATUS_META.termo_emitido
  if (avaliacao.status === "concluida") return STATUS_META.entrada_finalizada
  return STATUS_META.entrada_criada
}

export const getOficinaVehicleActions = (params: {
  veiculoId: string
  avaliacao?: OficinaAvaliacaoResumo | null
  diagnostico?: OficinaDiagnosticoResumo | null
  atendimento?: OficinaAtendimento
}) => {
  const { veiculoId, avaliacao, diagnostico } = params
  const atendimento = params.atendimento ?? getOficinaAtendimentoStatus(avaliacao, diagnostico ? [diagnostico] : [])
  const avaliacaoId = avaliacao?.id
  const diagnosticoHref = diagnostico ? `/diagnostico/${diagnostico.id}` : undefined
  const novoDiagnosticoHref = `/diagnostico/novo?veiculo=${veiculoId}${avaliacaoId ? `&avaliacao=${avaliacaoId}` : ""}`

  const secondary: OficinaVehicleAction[] = [
    { kind: "view_vehicle", label: "Ver veículo", href: `/veiculo/${veiculoId}`, variant: "outline" },
  ]

  if (!avaliacaoId) {
    return {
      primary: { kind: "start_entry", label: "Iniciar entrada", href: `/veiculo/${veiculoId}`, variant: "default" } satisfies OficinaVehicleAction,
      secondary,
      diagnostic: { kind: "create_diagnostic", label: "Novo diagnóstico", href: novoDiagnosticoHref, variant: "ghost" } satisfies OficinaVehicleAction,
    }
  }

  switch (atendimento.status) {
    case "entrada_criada":
      return {
        primary: { kind: "continue_entry", label: "Continuar entrada", href: `/avaliacao/${avaliacaoId}`, variant: "default" } satisfies OficinaVehicleAction,
        secondary: [{ kind: "view_entry", label: "Ver entrada", href: `/avaliacao/${avaliacaoId}`, variant: "outline" }],
        diagnostic: { kind: "create_diagnostic", label: "Criar diagnóstico", href: novoDiagnosticoHref, variant: "ghost" } satisfies OficinaVehicleAction,
      }
    case "entrada_finalizada":
      return {
        primary: { kind: "emit_term", label: "Emitir termo", href: `/proposta/${avaliacaoId}`, variant: "default" } satisfies OficinaVehicleAction,
        secondary: [{ kind: "view_entry", label: "Ver entrada", href: `/avaliacao/${avaliacaoId}`, variant: "outline" }],
        diagnostic: { kind: "create_diagnostic", label: "Criar diagnóstico", href: novoDiagnosticoHref, variant: "ghost" } satisfies OficinaVehicleAction,
      }
    case "termo_emitido":
      return {
        primary: { kind: "send_term", label: "Enviar termo", href: `/proposta/${avaliacaoId}`, variant: "default" } satisfies OficinaVehicleAction,
        secondary: [{ kind: "view_entry", label: "Ver entrada", href: `/avaliacao/${avaliacaoId}`, variant: "outline" }],
        diagnostic: { kind: "create_diagnostic", label: "Criar diagnóstico", href: novoDiagnosticoHref, variant: "ghost" } satisfies OficinaVehicleAction,
      }
    case "cliente_ciente_termo":
      return {
        primary: { kind: "finalize", label: "Finalizar atendimento", href: `/veiculo/${veiculoId}`, variant: "default" } satisfies OficinaVehicleAction,
        secondary: [{ kind: "view_entry", label: "Ver entrada", href: `/avaliacao/${avaliacaoId}`, variant: "outline" }],
        diagnostic: { kind: "create_diagnostic", label: "Criar diagnóstico se necessário", href: novoDiagnosticoHref, variant: "ghost" } satisfies OficinaVehicleAction,
      }
    case "diagnostico_criado":
      return {
        primary: { kind: "send_diagnostic", label: "Enviar diagnóstico", href: diagnosticoHref, variant: "default" } satisfies OficinaVehicleAction,
        secondary: [{ kind: "view_entry", label: "Ver entrada", href: `/avaliacao/${avaliacaoId}`, variant: "outline" }],
        diagnostic: { kind: "view_diagnostic", label: "Ver diagnóstico", href: diagnosticoHref, variant: "ghost" } satisfies OficinaVehicleAction,
      }
    case "diagnostico_enviado":
      return {
        primary: { kind: "wait_customer", label: "Aguardando cliente", href: diagnosticoHref, variant: "outline", disabled: false } satisfies OficinaVehicleAction,
        secondary: [{ kind: "view_entry", label: "Ver entrada", href: `/avaliacao/${avaliacaoId}`, variant: "outline" }],
        diagnostic: { kind: "view_diagnostic", label: "Ver diagnóstico", href: diagnosticoHref, variant: "ghost" } satisfies OficinaVehicleAction,
      }
    case "cliente_ciente_diagnostico":
    case "cliente_autorizou":
      return {
        primary: { kind: "finalize", label: "Finalizar atendimento", href: `/veiculo/${veiculoId}`, variant: "default" } satisfies OficinaVehicleAction,
        secondary: [{ kind: "view_entry", label: "Ver entrada", href: `/avaliacao/${avaliacaoId}`, variant: "outline" }],
        diagnostic: { kind: "view_diagnostic", label: "Ver diagnóstico", href: diagnosticoHref, variant: "ghost" } satisfies OficinaVehicleAction,
      }
    case "finalizado":
      return {
        primary: { kind: "view_service", label: "Ver atendimento", href: `/veiculo/${veiculoId}`, variant: "default" } satisfies OficinaVehicleAction,
        secondary: [{ kind: "view_entry", label: "Ver entrada", href: `/avaliacao/${avaliacaoId}`, variant: "outline" }],
        diagnostic: { kind: "create_diagnostic", label: "Novo diagnóstico", href: novoDiagnosticoHref, variant: "ghost" } satisfies OficinaVehicleAction,
      }
    default:
      return {
        primary: { kind: "view_service", label: atendimento.proximaAcao, href: `/veiculo/${veiculoId}`, variant: "default" } satisfies OficinaVehicleAction,
        secondary,
        diagnostic: { kind: "create_diagnostic", label: "Novo diagnóstico", href: novoDiagnosticoHref, variant: "ghost" } satisfies OficinaVehicleAction,
      }
  }
}

const getDiagnosticoPrioridade = (diagnostico: OficinaDiagnosticoResumo) => {
  if (diagnostico.cliente_autorizou_em || diagnostico.status === "autorizado") return 4
  if (diagnostico.cliente_ciente_em || diagnostico.status === "ciente") return 3
  if (diagnostico.share_token || diagnostico.status === "compartilhado") return 2
  return 1
}

export const normalizarTelefoneWhatsApp = (telefone?: string | null) => {
  const digitos = (telefone ?? "").replace(/\D/g, "")
  if (!digitos) return ""
  if (digitos.startsWith("55")) return digitos
  if (digitos.length >= 10) return `55${digitos}`
  return digitos
}
